import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { OrdersService } from '../../../services/patientmr/orders.service';
import { ApiError } from '../../../dtos/common/api.dto';
import { ORDER_DISCIPLINES, ORDER_TYPE_LABELS } from '../../../dtos/patientmr/orders.dto';
import type { CreateOrderRequest, OrderDiscipline, OrderType, PhysicianOrder } from '../../../dtos/patientmr/orders.dto';

export interface AddOrderDialogProps {
  open: boolean;
  patientId: string;
  /** Physician prefilled from the patient's PCP, when known */
  defaultPhysician?: { name: string; npi?: string };
  onClose: () => void;
  onCreated: (order: PhysicianOrder) => void;
}

const pad = (n: number): string => String(n).padStart(2, '0');

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Current local time in the `datetime-local` input format (YYYY-MM-DDTHH:mm) */
const nowLocal = (): string => {
  const d = new Date();
  return `${todayIso()}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export interface OrderFormValues {
  date: string;
  summary: string;
  physicianName: string;
  npi: string;
  type?: OrderType;
  takenAt?: string;
  contactPerson?: string;
  readBack?: boolean;
  service?: string;
  frequency?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}

/** Validates the add-order form. Returns a message or null. */
export function validateOrder(form: OrderFormValues): string | null {
  if (!form.date) return 'The order date is required.';
  if (!form.summary.trim()) return 'Summarize the order.';
  if (!form.physicianName.trim()) return 'The ordering physician is required.';
  if (form.npi && !/^\d{10}$/.test(form.npi)) return 'NPI must be 10 digits.';
  if (form.type === 'VERBAL') {
    if (!form.takenAt || Number.isNaN(new Date(form.takenAt).getTime())) return 'Enter the date and time the verbal order was taken.';
    if (!form.contactPerson?.trim()) return 'Enter who gave the order.';
    if (!form.service?.trim()) return 'Enter the service ordered.';
    if (!form.frequency?.trim()) return 'Enter the frequency.';
    if (form.readBack !== true) return 'Read the order back and confirm it before saving.';
  }
  if (form.effectiveFrom && form.effectiveTo && form.effectiveTo < form.effectiveFrom) {
    return 'The effective end date cannot be before the start date.';
  }
  return null;
}

/** Server validation messages (400 VALIDATION_FAILED details.errors), when present */
function serverErrors(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  const list = err.details?.errors;
  return Array.isArray(list) ? list.filter((e): e is string => typeof e === 'string') : [];
}

const AddOrderDialog: React.FC<AddOrderDialogProps> = ({ open, patientId, defaultPhysician, onClose, onCreated }) => {
  const baseId = useId();
  const [date, setDate] = useState(todayIso());
  const [type, setType] = useState<OrderType>('VERBAL');
  const [summary, setSummary] = useState('');
  const [physicianName, setPhysicianName] = useState('');
  const [npi, setNpi] = useState('');
  const [takenAt, setTakenAt] = useState(nowLocal());
  const [contactPerson, setContactPerson] = useState('');
  const [contactTouched, setContactTouched] = useState(false);
  const [readBack, setReadBack] = useState(false);
  const [discipline, setDiscipline] = useState<OrderDiscipline | ''>('');
  const [service, setService] = useState('');
  const [frequency, setFrequency] = useState('');
  const [duration, setDuration] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorList, setErrorList] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setDate(todayIso());
    setType('VERBAL');
    setSummary('');
    setPhysicianName(defaultPhysician?.name ?? '');
    setNpi(defaultPhysician?.npi ?? '');
    setTakenAt(nowLocal());
    setContactPerson(defaultPhysician?.name ?? '');
    setContactTouched(false);
    setReadBack(false);
    setDiscipline('');
    setService('');
    setFrequency('');
    setDuration('');
    setEffectiveFrom('');
    setEffectiveTo('');
    setError(null);
    setErrorList([]);
  }, [open, defaultPhysician]);

  const isVerbal = type === 'VERBAL';

  const handlePhysicianChange = (value: string) => {
    setPhysicianName(value);
    // "Order given by" follows the physician until the user edits it
    if (!contactTouched) setContactPerson(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateOrder({
      date, summary, physicianName, npi, type, takenAt, contactPerson, readBack, service, frequency, effectiveFrom, effectiveTo,
    });
    if (invalid) {
      setError(invalid);
      setErrorList([]);
      return;
    }
    setSaving(true);
    setError(null);
    setErrorList([]);
    const body: CreateOrderRequest = {
      date,
      type,
      summary: summary.trim(),
      orderingPhysician: { name: physicianName.trim(), ...(npi ? { npi } : {}) },
      ...(discipline ? { discipline } : {}),
      ...(service.trim() ? { service: service.trim() } : {}),
      ...(frequency.trim() ? { frequency: frequency.trim() } : {}),
      ...(duration.trim() ? { duration: duration.trim() } : {}),
      ...(effectiveFrom ? { effectiveFrom } : {}),
      ...(effectiveTo ? { effectiveTo } : {}),
      ...(isVerbal
        ? { takenAt: new Date(takenAt).toISOString(), contactPerson: contactPerson.trim(), readBack: true }
        : {}),
    };
    try {
      const order = await OrdersService.create(patientId, body);
      onCreated(order);
    } catch (err) {
      const list = serverErrors(err);
      setErrorList(list);
      setError(list.length
        ? 'The order was not saved:'
        : err instanceof Error && err.message ? err.message : 'The order was not saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm';
  const label = 'block text-sm font-medium text-slate-700';

  return (
    <Dialog open={open} onClose={() => { if (!saving) onClose(); }} labelledBy={`${baseId}-title`}>
      <form onSubmit={handleSubmit} className="space-y-3 text-left" noValidate>
        <h3 id={`${baseId}-title`} className="text-lg font-semibold text-slate-800 pr-6">Add physician order</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={label}>
            Order date
            <input aria-label="Order date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
          </label>
          <label className={label}>
            Type
            <select aria-label="Order type" value={type} onChange={(e) => setType(e.target.value as OrderType)} className={input}>
              {(Object.keys(ORDER_TYPE_LABELS) as OrderType[]).map((t) => (
                <option key={t} value={t}>{ORDER_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </label>
        </div>
        <label className={label}>
          Summary
          <textarea aria-label="Order summary" rows={3} maxLength={1000} value={summary} onChange={(e) => setSummary(e.target.value)} className={input}
            placeholder="e.g. Increase PDN to 12 hours/day for 2 weeks" />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={label}>
            Ordering physician
            <input aria-label="Ordering physician" type="text" value={physicianName} onChange={(e) => handlePhysicianChange(e.target.value)} className={input} />
          </label>
          <label className={label}>
            NPI
            <input aria-label="Physician NPI" type="text" inputMode="numeric" maxLength={10} value={npi}
              onChange={(e) => setNpi(e.target.value.replace(/\D/g, ''))} className={input} />
          </label>
        </div>

        {isVerbal && (
          <fieldset className="space-y-3 rounded-md border border-slate-200 p-3" data-testid="verbal-order-fields">
            <legend className="px-1 text-sm font-semibold text-slate-700">Verbal order</legend>
            <p className="text-sm text-slate-600" data-testid="taken-by">Taken by: you</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className={label}>
                Date and time taken
                <input aria-label="Date and time taken" type="datetime-local" value={takenAt} onChange={(e) => setTakenAt(e.target.value)} className={input} />
              </label>
              <label className={label}>
                Order given by
                <input aria-label="Order given by" type="text" maxLength={120} value={contactPerson}
                  onChange={(e) => { setContactTouched(true); setContactPerson(e.target.value); }} className={input} />
              </label>
            </div>
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-slate-700 sm:min-h-0">
              <input type="checkbox" checked={readBack} onChange={(e) => setReadBack(e.target.checked)} className="h-4 w-4" />
              Read back and confirmed
            </label>
            <p className="text-xs text-slate-600">A verbal order needs the physician&apos;s signed copy; it shows as unsigned until uploaded.</p>
          </fieldset>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={label}>
            Service{isVerbal ? '' : ' (optional)'}
            <input aria-label="Service" type="text" maxLength={200} value={service} onChange={(e) => setService(e.target.value)} className={input}
              placeholder="e.g. Skilled nursing visits" />
          </label>
          <label className={label}>
            Frequency{isVerbal ? '' : ' (optional)'}
            <input aria-label="Frequency" type="text" maxLength={200} value={frequency} onChange={(e) => setFrequency(e.target.value)} className={input}
              placeholder="e.g. 12 hours/day, 5 days/week" />
          </label>
          <label className={label}>
            Discipline (optional)
            <select aria-label="Discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value as OrderDiscipline | '')} className={input}>
              <option value="">Not specified</option>
              {ORDER_DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className={label}>
            Duration (optional)
            <input aria-label="Duration" type="text" maxLength={120} value={duration} onChange={(e) => setDuration(e.target.value)} className={input}
              placeholder="e.g. 2 weeks" />
          </label>
          <label className={label}>
            Effective from (optional)
            <input aria-label="Effective from" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className={input} />
          </label>
          <label className={label}>
            Effective to (optional)
            <input aria-label="Effective to" type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} className={input} />
          </label>
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-700">
            <p>{error}</p>
            {errorList.length > 0 && (
              <ul className="ml-5 list-disc">
                {errorList.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={saving} className="min-h-[44px] rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="min-h-[44px] rounded-md bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Add order'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddOrderDialog;
